import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Play, GraduationCap, Users, BarChart2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import "./PopupModal.scss";
import sharkTankImage from "../assets/cg_shark.jpg";
// the half-size copy: the popup never shows the poster wider than ~450px,
// and this is the home page, so the full 1400px file is not worth sending
import youngFoundersPoster from "../assets/young-founders/poster-sm.jpg";
import AsOfNote from "./AsOfNote";
import { impact } from "./../data/stats";
import { youngFounders, youngFoundersState } from "../data/youngFounders";

// ─── Home-page popup ─────────────────────────────────────────────────────────
// Two variants on the one layout. While entries for the Young Founders Special
// are open the popup leads with that, because it is the time-bound ask; once
// they close it reverts to the evergreen incubation pitch on its own, with
// nobody editing this file.

const INCUBATION = {
  image: sharkTankImage,
  badge: "As seen on Shark Tank India",
  badgeIcon: GraduationCap,
  // `short` here, not `display`: the popup's stat row is narrow. Both forms
  // come from the same number in data/stats.js.
  stats: [
    { value: impact.schools.short,   label: "Schools"   },
    { value: impact.students.short,  label: "Students"  },
    { value: impact.incubated.short, label: "Incubated" },
  ],
  chip: "2026 Cohort · Applications Open",
  heading: (
    <>Transform Your School<br />into a <span>High-Performance</span><br />Institution</>
  ),
  sub: "CoGrad embeds inside your school and rebuilds it end-to-end — through systems, training, and AI technology. Not advice. Real results.",
  feats: [
    { Icon: Users,     label: `${impact.schools.display} ${impact.schools.label}` },
    { Icon: BarChart2, label: "Live Analytics" },
  ],
  primary: { label: "Apply for Incubation", Icon: ArrowUpRight, href: "/incubation/register" },
  ghost:   { label: "Watch Our Shark Tank Pitch", Icon: Play, external: "https://youtu.be/jMkCnEDCJo8" },
  note: "Limited seats per cohort · No obligation to apply",
};

// CoGrad is only the outreach partner for this one, so the poster is shown
// whole — it carries the "Outreach Partner" line that states our role — and
// none of the copy beside it is written as though the campaign were ours. The
// video is our own pitch, which is why it sits on the other panel: it is the
// reason to trust the detour through us.
const YOUNG_FOUNDERS = {
  kind: "yf",
  image: youngFoundersPoster,
  imageAlt:
    `Got a business idea? Entries are open till 30th October. Eligibility: ` +
    `${youngFounders.eligibility} years old. Tap to see the details.`,
  chip: `Closes ${youngFounders.closesLabel}`,
  heading: (
    <>Entries Are Open for<br /><span>Young Founders.</span></>
  ),
  sub: `Open to students ${youngFounders.eligibility}. CoGrad is the outreach partner, so you can register through us.`,
  video: {
    id: "jMkCnEDCJo8",
    poster: sharkTankImage,
    title: "Watch CoGrad's own pitch on Shark Tank India",
  },
  // Leads to our own page rather than straight out to SonyLIV: the eligibility,
  // the four rounds and the fee are all there, and the apply button on it goes
  // to the official site. A visitor who lands cold on the form bounces.
  primary: { label: "See the details", Icon: ArrowUpRight, href: youngFounders.path },
  note: "Eligibility, dates and how to apply · Registration is free",
};

const PopupModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);

  const v = youngFoundersState() === "open" ? YOUNG_FOUNDERS : INCUBATION;
  const BadgeIcon = v.badgeIcon;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      sessionStorage.setItem("cogradPopupSeen", "true");
    } else {
      document.body.style.overflow = "";
      setPlaying(false);
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleClose = () => onClose?.();

  const go = (action) => () => {
    if (action.external) window.open(action.external, "_blank", "noopener,noreferrer");
    else navigate(action.href);
    handleClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="pm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            className={v.kind === "yf" ? "pm pm--yf" : "pm"}
            initial={{ opacity: 0, scale: 0.9, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* ── LEFT — image panel ── */}
            {v.kind === "yf" ? (
              <Link
                className="pm__left pm__left--poster"
                to={v.primary.href}
                onClick={handleClose}
                aria-label={v.primary.label}
              >
                <img src={v.image} alt="" className="pm__bg-blur" />
                <img src={v.image} alt={v.imageAlt} className="pm__bg" />
              </Link>
            ) : (
            <div className="pm__left">
              <img src={v.image} alt="" className="pm__bg-blur" />
              <img src={v.image} alt="" className="pm__bg" />
              <div className="pm__left-overlay" />

              <div className="pm__left-content">
                <div className="pm__left-body">
                  <div className="pm__sti-badge">
                    <BadgeIcon size={12} strokeWidth={2.2} />
                    {v.badge}
                  </div>

                  <div className="pm__stats-row">
                    {v.stats.map((s, i) => (
                      <React.Fragment key={s.label}>
                        <div className="pm__stat">
                          <span className="pm__stat-val">{s.value}</span>
                          <span className="pm__stat-lbl">{s.label}</span>
                        </div>
                        {i < v.stats.length - 1 && <div className="pm__stat-sep" />}
                      </React.Fragment>
                    ))}
                  </div>
                  <AsOfNote className="asof--center pm__asof" />
                </div>
              </div>
            </div>
            )}

            {/* ── RIGHT — CTA panel ── */}
            <div className="pm__right">
              <button className="pm__close" onClick={handleClose} aria-label="Close">
                <X size={17} strokeWidth={2.5} />
              </button>

              <div className="pm__chip">
                <span className="pm__chip-dot" />
                {v.chip}
              </div>

              <h2 className="pm__h2">{v.heading}</h2>

              <p className="pm__sub">{v.sub}</p>

              {v.feats && (
                <div className="pm__feats">
                  {v.feats.map(({ Icon, label }) => (
                    <span className="pm__feat" key={label}>
                      <Icon size={12} strokeWidth={2.5} /> {label}
                    </span>
                  ))}
                </div>
              )}

              {/* The iframe is only mounted on click, so the popup still opens
                  without pulling anything from YouTube. */}
              {v.video && (
                <div className="pm__video">
                  {playing ? (
                    <iframe
                      className="pm__video-frame"
                      src={`https://www.youtube-nocookie.com/embed/${v.video.id}?autoplay=1&rel=0`}
                      title={v.video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      className="pm__video-cover"
                      onClick={() => setPlaying(true)}
                    >
                      <img src={v.video.poster} alt="" />
                      <span className="pm__video-play">
                        <Play size={15} fill="currentColor" strokeWidth={0} />
                      </span>
                      <span className="pm__video-cap">{v.video.title}</span>
                    </button>
                  )}
                </div>
              )}

              <div className="pm__btns">
                <button className="pm__btn pm__btn--fill" onClick={go(v.primary)}>
                  {v.primary.label}
                  {v.primary.Icon && <v.primary.Icon size={16} strokeWidth={2.5} />}
                </button>
                {v.ghost && (
                  <button className="pm__btn pm__btn--ghost" onClick={go(v.ghost)}>
                    {v.ghost.Icon && <v.ghost.Icon size={13} fill="currentColor" strokeWidth={0} />}
                    {v.ghost.label}
                  </button>
                )}
              </div>

              <p className="pm__note">{v.note}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PopupModal;
